import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaCheck, FaEdit, FaMinus, FaPlus } from "react-icons/fa";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { CreateCampaignFlowValues } from "../../forms/campaignFlowForm";
import IconWrapper from "../../components/IconWrapper";

const GENDER_OPTIONS = [
  { key: "all", value: "all" },
  { key: "male", value: "male" },
  { key: "female", value: "female" },
];

export type GuidedTargetingSearch = {
  interests: (
    query: string
  ) => Promise<{ data: { id: string; name: string }[] }>;
  geo_markets: (query: string) => Promise<{
    data: {
      key: string;
      name: string;
      country_code?: string;
      country_name?: string;
    }[];
  }>;
};

type GuidedTargetingFieldsProps = {
  search?: GuidedTargetingSearch | null;
  isLoading?: boolean;
  /** Increment to open the interests editor (e.g. from Selected audiences → Edit). */
  openInterestsEditKey?: number;
};

export default function GuidedTargetingFields({
  search,
  isLoading = false,
  openInterestsEditKey = 0,
}: GuidedTargetingFieldsProps) {
  const { setValue, watch, getValues } =
    useFormContext<CreateCampaignFlowValues>();
  const [isEditingLocations, setIsEditingLocations] = useState(false);
  const [isEditingInterests, setIsEditingInterests] = useState(false);
  const lastOpenInterestsKey = useRef(0);

  useEffect(() => {
    if (
      openInterestsEditKey > 0 &&
      openInterestsEditKey !== lastOpenInterestsKey.current
    ) {
      lastOpenInterestsKey.current = openInterestsEditKey;
      setIsEditingInterests(true);
    }
  }, [openInterestsEditKey]);
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [interestSearchInput, setInterestSearchInput] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState<
    { id: string; key: string; name: string; value: string; country?: string }[]
  >([]);
  const [interestSearchResults, setInterestSearchResults] = useState<
    { id: string; name: string }[]
  >([]);

  const geoMarkets = watch("geo_markets") ?? [];
  const countries = watch("countries") ?? [];
  const interests = watch("interests") ?? [];

  const locationOptions = [
    ...geoMarkets.map((g: { key: string; name?: string; value?: string }) => ({
      type: "geo_markets",
      value: g.key,
      name: g.name ?? g.value ?? g.key,
    })),
    ...countries.map((c: { key: string; value?: string }) => ({
      type: "countries",
      value: c.key,
      name: c.value ?? c.key,
    })),
  ];
  const interestOptions = interests.map(
    (i: { key: string; value: string }) => ({
      type: "interests",
      value: i.key,
      key: i.key,
      name: i.value,
    })
  );

  const searchLocations = useCallback(
    (query: string) => {
      const q = typeof query === "string" ? query : "";
      if (!search?.geo_markets || !q.trim()) {
        setLocationSearchResults([]);
        return Promise.resolve();
      }
      return search
        .geo_markets(q.trim())
        .then((res) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          setLocationSearchResults(
            data.map(
              (r: {
                key?: string;
                name?: string;
                country_code?: string;
                country_name?: string;
              }) => ({
                id: r.key ?? "",
                key: r.key ?? "",
                name: `${r.name ?? ""} (${r.country_name ?? r.country_code ?? ""})`,
                value: `${r.name ?? ""} (${r.country_name ?? r.country_code ?? ""})`,
                country: r.country_code,
              })
            )
          );
        })
        .catch(() => {
          setLocationSearchResults([]);
        });
    },
    [search]
  );

  const searchInterests = useCallback(
    (query: string) => {
      const q = typeof query === "string" ? query : "";
      if (!search?.interests || !q.trim()) {
        setInterestSearchResults([]);
        return Promise.resolve();
      }
      return search
        .interests(q.trim())
        .then((data) => {
          const list = Array.isArray(data?.data) ? data.data : [];
          setInterestSearchResults(list);
        })
        .catch(() => {
          setInterestSearchResults([]);
        });
    },
    [search]
  );

  const addLocation = useCallback(
    (item: {
      key: string;
      name: string;
      country?: string;
      type: "geo_markets" | "countries";
    }) => {
      if (item.type === "geo_markets") {
        const current = getValues("geo_markets") ?? [];
        if (current.some((g: { key: string }) => g.key === item.key)) return;
        setValue(
          "geo_markets",
          [
            ...current,
            {
              key: item.key,
              value: item.name,
              name: item.name,
              country: item.country ?? "",
            },
          ],
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        );
      } else {
        const current = getValues("countries") ?? [];
        if (current.some((c: { key: string }) => c.key === item.key)) return;
        setValue(
          "countries",
          [...current, { key: item.key, value: item.name }],
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        );
      }
    },
    [getValues, setValue]
  );

  const removeLocation = useCallback(
    (item: { type: string; key?: string; value?: string }) => {
      const key = item.key ?? item.value;
      if (item.type === "geo_markets") {
        const current = getValues("geo_markets") ?? [];
        setValue(
          "geo_markets",
          current.filter((g: { key: string }) => g.key !== key),
          { shouldDirty: true, shouldValidate: true }
        );
      } else {
        const current = getValues("countries") ?? [];
        setValue(
          "countries",
          current.filter((c: { key: string }) => c.key !== key),
          { shouldDirty: true, shouldValidate: true }
        );
      }
    },
    [getValues, setValue]
  );

  const addInterest = useCallback(
    (interest: { id: string; name: string }) => {
      const current = getValues("interests") ?? [];
      if (current.some((i: { key: string }) => i.key === interest.id)) return;
      setValue(
        "interests",
        [...current, { key: interest.id, value: interest.name }],
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [getValues, setValue]
  );

  const removeInterest = useCallback(
    (item: { key?: string; value?: string }) => {
      const key = item.key ?? item.value;
      const current = getValues("interests") ?? [];
      setValue(
        "interests",
        current.filter((i: { key: string }) => i.key !== key),
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [getValues, setValue]
  );

  const isLocationSelected = (key: string) =>
    locationOptions.some((t) => t.value === key);
  const isInterestSelected = (id: string) =>
    interestOptions.some((i) => i.key === id);

  return (
    <VStack w="full" spacing={4} align="stretch">
      {/* Locations */}
      <Box
        p={4}
        rounded="xl"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
      >
        <Stack
          w="full"
          justifyContent="space-between"
          alignItems="center"
          direction={[isEditingLocations ? "column" : "row"]}
        >
          {isEditingLocations ? (
            <VStack w="full" spacing={3}>
              <HStack w="full">
                <Input
                  placeholder="Search locations"
                  size="md"
                  bg="white"
                  value={locationSearchInput}
                  onChange={(e) => setLocationSearchInput(e.target.value)}
                  isDisabled={isLoading}
                  flexGrow={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      searchLocations(locationSearchInput);
                    }
                  }}
                />
                <Button
                  flexShrink={0}
                  isLoading={isLoading}
                  onClick={() => searchLocations(locationSearchInput)}
                >
                  Search
                </Button>
              </HStack>
              <VStack w="full" maxH="200px" overflowY="auto" spacing={2}>
                {locationSearchResults.map((t) => (
                  <HStack key={t.id} w="full" justify="space-between">
                    <Text fontSize="sm">{t.name}</Text>
                    <IconButton
                      size="xs"
                      aria-label={isLocationSelected(t.key) ? "Remove" : "Add"}
                      icon={
                        isLocationSelected(t.key) ? <FaMinus /> : <FaPlus />
                      }
                      onClick={() =>
                        isLocationSelected(t.key)
                          ? removeLocation({
                              type: "geo_markets",
                              key: t.key,
                              value: t.name,
                            })
                          : addLocation({
                              type: "geo_markets",
                              key: t.key,
                              name: t.name,
                              country: t.country,
                            })
                      }
                    />
                  </HStack>
                ))}
              </VStack>
              <HStack w="full" gap={2} flexWrap="wrap">
                {locationOptions.map((t) => (
                  <IconWrapper
                    key={`${t.type}-${t.value}`}
                    item={t}
                    isLoading={isLoading}
                    onRemove={removeLocation}
                  />
                ))}
              </HStack>
            </VStack>
          ) : (
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="bold" fontSize="md" color="gray.800">
                Locations
              </Text>
              {locationOptions.length > 0 ? (
                <Text fontSize="sm" color="gray.600">
                  Inclusion:{" "}
                  {locationOptions.map((t) => t.name || t.value).join(", ")}
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No locations selected
                </Text>
              )}
            </VStack>
          )}
          <Button
            size="sm"
            variant="ghost"
            leftIcon={
              isEditingLocations ? <FaCheck size={12} /> : <FaEdit size={12} />
            }
            colorScheme="gray"
            onClick={() => setIsEditingLocations(!isEditingLocations)}
          >
            {isEditingLocations ? "Done" : "Edit"}
          </Button>
        </Stack>
      </Box>

      {/* Age & Gender */}
      <HStack w="full" spacing={4} align="stretch">
        <Box
          p={4}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
          flexGrow={1}
        >
          <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
            Age
          </Text>
          <HStack w="full" spacing={2}>
            <Select
              value={watch("age_min") ?? 18}
              onChange={(e) =>
                setValue("age_min", Number(e.target.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              size="md"
              bg="white"
              borderRadius="md"
            >
              {Array.from({ length: 83 }, (_, i) => i + 13).map((age) => (
                <option key={age} value={age}>
                  {age}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              -
            </Text>
            <Select
              value={watch("age_max") ?? 65}
              onChange={(e) =>
                setValue("age_max", Number(e.target.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              size="md"
              bg="white"
              borderRadius="md"
            >
              {Array.from({ length: 83 }, (_, i) => i + 13).map((age) => (
                <option key={age} value={age}>
                  {age}
                </option>
              ))}
            </Select>
          </HStack>
        </Box>
        <Box
          p={4}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
          flexGrow={1}
        >
          <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
            Gender
          </Text>
          <Select
            value={watch("genders") ?? "all"}
            onChange={(e) =>
              setValue("genders", e.target.value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            size="md"
            bg="white"
            borderRadius="md"
          >
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.value}
              </option>
            ))}
          </Select>
        </Box>
      </HStack>

      {/* Interests */}
      <Box
        p={4}
        rounded="xl"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
      >
        <Stack
          w="full"
          justifyContent="space-between"
          alignItems="center"
          direction={[isEditingInterests ? "column" : "row"]}
        >
          {isEditingInterests ? (
            <VStack w="full" spacing={3}>
              <HStack w="full">
                <Input
                  placeholder="Search interests"
                  size="md"
                  bg="white"
                  value={interestSearchInput}
                  onChange={(e) => setInterestSearchInput(e.target.value)}
                  isDisabled={isLoading}
                  flexGrow={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      searchInterests(interestSearchInput);
                    }
                  }}
                />
                <Button
                  flexShrink={0}
                  isLoading={isLoading}
                  onClick={() => searchInterests(interestSearchInput)}
                >
                  Search
                </Button>
              </HStack>
              <VStack w="full" maxH="200px" overflowY="auto" spacing={2}>
                {interestSearchResults.map((interest) => (
                  <HStack key={interest.id} w="full" justify="space-between">
                    <Text fontSize="sm">{interest.name}</Text>
                    <IconButton
                      size="xs"
                      aria-label={
                        isInterestSelected(interest.id) ? "Remove" : "Add"
                      }
                      icon={
                        isInterestSelected(interest.id) ? (
                          <FaMinus />
                        ) : (
                          <FaPlus />
                        )
                      }
                      onClick={() =>
                        isInterestSelected(interest.id)
                          ? removeInterest({
                              key: interest.id,
                              value: interest.name,
                            })
                          : addInterest(interest)
                      }
                    />
                  </HStack>
                ))}
              </VStack>
              <HStack w="full" gap={2} flexWrap="wrap">
                {interestOptions.map((item, idx) => (
                  <IconWrapper
                    key={item.key ?? idx}
                    item={item}
                    isLoading={isLoading}
                    onRemove={removeInterest}
                  />
                ))}
              </HStack>
            </VStack>
          ) : (
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="bold" fontSize="md" color="gray.800">
                Interests
              </Text>
              {interestOptions.length > 0 ? (
                <Text fontSize="sm" color="gray.600">
                  {interestOptions.length} interest
                  {interestOptions.length !== 1 ? "s" : ""} selected
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No interests selected
                </Text>
              )}
            </VStack>
          )}
          <Button
            size="sm"
            variant="ghost"
            leftIcon={
              isEditingInterests ? <FaCheck size={12} /> : <FaEdit size={12} />
            }
            colorScheme="gray"
            onClick={() => setIsEditingInterests(!isEditingInterests)}
          >
            {isEditingInterests ? "Done" : "Edit"}
          </Button>
        </Stack>
      </Box>
    </VStack>
  );
}
